import { lazy, Suspense } from 'react';
import { Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import { useMe } from '../../features/auth/api.js';
import { useThemeMode } from '../../features/settings/themePreference.js';
import { useShellFrame } from './shellFrame.js';
import { BrandLogo, brandForUser } from '../BrandLogo.js';

const GlobalSearch = lazy(() =>
  import('./GlobalSearch.js').then((m) => ({ default: m.GlobalSearch })),
);

const NotificationBar = lazy(() =>
  import('./NotificationBar.js').then((m) => ({ default: m.NotificationBar })),
);

const QuickTaskButton = lazy(() =>
  import('./QuickTaskButton.js').then((m) => ({ default: m.QuickTaskButton })),
);

interface Props {
  onMenuClick: () => void;
  onToggleCollapse: () => void;
  collapsed: boolean;
  sidebarWidth: number;
}

export function TopBar({ onMenuClick, onToggleCollapse, collapsed, sidebarWidth }: Props) {
  const { title, actions } = useShellFrame();
  const me = useMe();
  const [themeMode, setThemeMode] = useThemeMode();
  const brand = brandForUser(me.data);
  const nextThemeMode = themeMode === 'dark' ? 'light' : 'dark';
  return (
    <header className="topbar-shell glass-topbar" style={{ left: sidebarWidth, transition: 'left 250ms ease' }}>
      <button
        type="button"
        className="btn btn-ghost btn-icon show-mobile-flex"
        onClick={onMenuClick}
        aria-label="Open menu"
      >
        <Menu />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon hide-mobile"
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
      </button>
      <BrandLogo variant="mark" tone="light" brand={brand} className="topbar-brand-mark show-mobile-flex" />
      {title && (
        <h1 className="topbar-title" style={{ fontSize: 16, fontWeight: 600, margin: 0, color: 'var(--text)' }}>
          {title}
        </h1>
      )}
      <Suspense fallback={null}>
        <NotificationBar />
      </Suspense>
      <div className="topbar-search-slot">
        <Suspense fallback={<div className="global-search-skeleton" aria-hidden="true" />}>
          <GlobalSearch />
        </Suspense>
      </div>
      <div className="topbar-actions">
        <button
          type="button"
          className="btn btn-ghost btn-icon theme-mode-toggle"
          onClick={() => setThemeMode(nextThemeMode)}
          aria-label={`Switch to ${nextThemeMode} mode`}
          aria-pressed={themeMode === 'dark'}
          title={`Switch to ${nextThemeMode} mode`}
        >
          {themeMode === 'dark' ? <Sun /> : <Moon />}
        </button>
        <Suspense fallback={null}>
          <QuickTaskButton />
        </Suspense>
        {actions}
      </div>
    </header>
  );
}
